import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Audience types supported by the application
 */
export type AudienceType = 'highschool' | 'highered';

/**
 * AudienceService - Domain-based audience detection and routing
 * 
 * Determines the audience (highschool or highered) based on the current domain.
 * Used for:
 * - Restricting content access based on domain
 * - Determining homepage URL path
 * - Validating URL audience matches domain audience
 * 
 * Domain Mapping:
 * - *.bfwpub.* → highschool
 * - *.macmillan-learning.* → highered
 * - *.macmillanlearning.* → highered
 * - localhost → configurable (default: highered)
 */
@Injectable({
  providedIn: 'root'
})
export class AudienceService {
  private _audience: AudienceType;
  private _hostname: string;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this._hostname = this.getHostname();
    this._audience = this.detectAudienceFromDomain();
  }

  /**
   * Get the current hostname
   */
  private getHostname(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.hostname.toLowerCase();
    }
    // SSR fallback - could be enhanced to read from request headers
    return 'localhost';
  }

  /**
   * Detect audience based on domain patterns
   */
  private detectAudienceFromDomain(): AudienceType {
    const hostname = this._hostname;

    // High School domains (bfwpub)
    if (hostname.includes('bfwpub')) {
      return 'highschool';
    }

    // Higher Ed domains (macmillan)
    if (hostname.includes('macmillan-learning') || 
        hostname.includes('macmillanlearning') ||
        hostname.includes('macmillan')) {
      return 'highered';
    }

    // Localhost development - default to highered, can be overridden
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // Check for localStorage override for development
      if (isPlatformBrowser(this.platformId)) {
        const devAudience = localStorage.getItem('dev_audience');
        if (devAudience === 'highschool' || devAudience === 'highered') {
          console.log(`🔧 Dev mode: Using audience override "${devAudience}" from localStorage`);
          return devAudience;
        }
      }
      // Default for localhost
      return 'highered';
    }

    // Unknown domain - default to highered
    console.warn(`⚠️ Unknown domain "${hostname}", defaulting to "highered"`);
    return 'highered';
  }

  /**
   * Get the current audience based on domain
   */
  get audience(): AudienceType {
    return this._audience;
  }

  /**
   * Get the current hostname
   */
  get hostname(): string {
    return this._hostname;
  }

  /**
   * Get the homepage path for the current audience
   */
  get homepagePath(): string {
    return `/${this._audience}`;
  }

  /**
   * Check if a given audience matches the current domain's audience
   * @param urlAudience - The audience from the URL path
   * @returns true if the URL audience matches the domain audience
   */
  isValidAudience(urlAudience: string): boolean {
    return urlAudience === this._audience;
  }

  /**
   * Check if the current domain is a high school domain
   */
  isHighSchool(): boolean {
    return this._audience === 'highschool';
  }

  /**
   * Check if the current domain is a higher ed domain
   */
  isHigherEd(): boolean {
    return this._audience === 'highered';
  }

  /**
   * Set audience for development/testing (localStorage)
   * Only works in browser environment
   */
  setDevAudience(audience: AudienceType): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('dev_audience', audience);
      console.log(`🔧 Dev audience set to "${audience}". Reload the page to apply.`);
    }
  }

  /**
   * Clear development audience override
   */
  clearDevAudience(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('dev_audience');
      console.log('🔧 Dev audience cleared. Reload the page to use domain-based detection.');
    }
  }
}

